import React from 'react'
import { Form } from 'semantic-ui-react'

import FollowupConversationOptionCheckbox from './FollowupConversationOptionCheckbox'
import ActivateIntegrationButtonSection from 'components/ActivateIntegrationButtonSection'

/**
 * @typedef {object} IntegrationSetting
 * @property {number} id
 * @property {string} name
 * @property {string} type
 * @property {boolean} is_active
 *
 * @typedef {object} SurveyIntegrationSetting
 * @property {string} name
 * @property {string} integration_type
 * @property {object} survey
 * @property {object} additional_option
 *
 * @typedef {object} PushFollowupConversationSetting
 * @property {boolean} is_positive_feedback_with_comment_enabled
 * @property {boolean} is_positive_feedback_without_comment_enabled
 * @property {boolean} is_neutral_feedback_with_comment_enabled
 * @property {boolean} is_neutral_feedback_without_comment_enabled
 * @property {boolean} is_negative_feedback_with_comment_enabled
 * @property {boolean} is_negative_feedback_without_comment_enabled
 *
 * @param {object} props
 * @param {IntegrationSetting} props.integrationSetting
 * @param {function} props.activateIntegration
 * @param {function} props.deactivateIntegration
 * @param {boolean} props.updateCompanyIntegrationSettingAPILoading
 * @param {boolean} props.isAuthenticated
 * @param {SurveyIntegrationSetting[]} props.surveyIntegrationSettings
 */
export default function IntercomCreateFollowupConversationIntegration({
  integrationSetting,
  activateIntegration,
  deactivateIntegration,
  updateCompanyIntegrationSettingAPILoading,
  isAuthenticated,
  surveyIntegrationSettings,
}) {
  const isIntegrationActive = integrationSetting.is_active

  return (
    <React.Fragment>
      <div className="grid grid-cols-2 mb3">
        <div>
          <p>
            Automated follow-up conversations make it easy for your team to quickly respond to
            customers and close the loop - all within your team's existing support workflows.
          </p>
          <p>
            <a
              href="https://help.simplesat.io/en/articles/5230032-intercom-integration#h_310e6a3198"
              target="_blank"
              rel="noreferrer"
            >
              <i className="icon far fa-book" />
              Learn more about creating follow-up conversations from feedback
            </a>
          </p>

          <ActivateIntegrationButtonSection
            activateIntegration={activateIntegration}
            deactivateIntegration={deactivateIntegration}
            loading={updateCompanyIntegrationSettingAPILoading}
            isAuthenticated={isAuthenticated}
            isIntegrationActive={isIntegrationActive}
            integrationType="Intercom"
          />
        </div>
        <div>
          <img
            src="https://cdn.simplesat.io/intercom/create-follow-up-conversations-from-feedback.png"
            alt="Create follow-up conversations from feedback"
            className="ui bordered image float-right"
          />
        </div>
      </div>

      <div>
        <h4>Surveys</h4>
        <div className="grid grid-cols-1 md:grid-cols-3">
          {surveyIntegrationSettings.map((surveyIntegrationSetting, index) => (
            <SurveyIntegrationSetting
              key={index}
              surveyIntegrationSetting={surveyIntegrationSetting}
              isIntegrationActive={isIntegrationActive}
              isAuthenticated={isAuthenticated}
            />
          ))}
        </div>
      </div>
    </React.Fragment>
  )
}

function SurveyIntegrationSetting({
  surveyIntegrationSetting,
  isIntegrationActive,
  isAuthenticated,
}) {
  const {
    is_positive_feedback_with_comment_enabled,
    is_positive_feedback_without_comment_enabled,
    is_neutral_feedback_with_comment_enabled,
    is_neutral_feedback_without_comment_enabled,
    is_negative_feedback_with_comment_enabled,
    is_negative_feedback_without_comment_enabled,
  } = surveyIntegrationSetting.additional_option

  const disabled = !isAuthenticated || !isIntegrationActive
  return (
    <div className="mb3">
      <Form>
        <Form.Group grouped>
          <h4>{surveyIntegrationSetting.survey.name}</h4>
          <Form.Field>
            <FollowupConversationOptionCheckbox
              defaultIsActive={is_positive_feedback_with_comment_enabled}
              label="Positive with comment"
              name="is_positive_feedback_with_comment_enabled"
              survey={surveyIntegrationSetting.survey}
              disabled={disabled}
            />
          </Form.Field>
          <Form.Field>
            <FollowupConversationOptionCheckbox
              defaultIsActive={is_positive_feedback_without_comment_enabled}
              label="Positive without comment"
              name="is_positive_feedback_without_comment_enabled"
              survey={surveyIntegrationSetting.survey}
              disabled={disabled}
            />
          </Form.Field>
          <Form.Field>
            <FollowupConversationOptionCheckbox
              defaultIsActive={is_neutral_feedback_with_comment_enabled}
              label="Neutral with comment"
              name="is_neutral_feedback_with_comment_enabled"
              survey={surveyIntegrationSetting.survey}
              disabled={disabled}
            />
          </Form.Field>
          <Form.Field>
            <FollowupConversationOptionCheckbox
              defaultIsActive={is_neutral_feedback_without_comment_enabled}
              label="Neutral without comment"
              name="is_neutral_feedback_without_comment_enabled"
              survey={surveyIntegrationSetting.survey}
              disabled={disabled}
            />
          </Form.Field>
          <Form.Field>
            <FollowupConversationOptionCheckbox
              defaultIsActive={is_negative_feedback_with_comment_enabled}
              label="Negative with comment"
              name="is_negative_feedback_with_comment_enabled"
              survey={surveyIntegrationSetting.survey}
              disabled={disabled}
            />
          </Form.Field>
          <Form.Field>
            <FollowupConversationOptionCheckbox
              defaultIsActive={is_negative_feedback_without_comment_enabled}
              label="Negative without comment"
              name="is_negative_feedback_without_comment_enabled"
              survey={surveyIntegrationSetting.survey}
              disabled={disabled}
            />
          </Form.Field>
        </Form.Group>
      </Form>
    </div>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/IntercomCreateFollowupConversationIntegration/index.js